import React from 'react';
import {
    GridColDef,
    GridColTypeDef,
    getGridStringOperators,
    GridSortDirection,
    GridCellParams,
    GridColumnHeaderParams
} from '@mui/x-data-grid-pro';
import { ShutdownStatus } from '../../lib/types';
import { formatToLocalTime } from '../../lib/helpers/helpers';
import { DateTime } from 'luxon';
import { customDateFilterOperators } from './CustomDataGridFilters';
import { SITEID_FACILITY_MANAGER_LINK } from '../../lib/globals';
import { OverlayTrigger, Popover } from 'react-bootstrap';
import { AiOutlineInfoCircle } from 'react-icons/ai';
import { FILTER_TIP_ICON_SIZE, SITE_SHUTDOWN_PRECOG_TICKET_CREATION_LINK } from '../../lib/constants';
import { ButtonProps } from '@mui/material/Button';
import ExportIcon from '@mui/icons-material/SaveAlt';

export type DateRange = {
    from?: Date | string;
    to?: Date | string;
};

// Helper function to get status color
export const getStatusColor = (status: ShutdownStatus): string => {
    switch (status) {
        case ShutdownStatus.SCHEDULED:
            return '#1E3F66';
        case ShutdownStatus.UPCOMING:
            return '#D4AF37';
        case ShutdownStatus.ONGOING:
            return '#8B0000 ';
        case ShutdownStatus.REOPENED:
            return '#CC5500';
        case ShutdownStatus.COMPLETED:
            return '#228B22';
        case ShutdownStatus.CANCELED:
            return '#6C757D';
        default:
            return '#000000';
    }
};

export const durationToString = (durationSeconds: number): string => {
    const secondsInMinute = 60;
    const secondsInHour = secondsInMinute * 60;
    const secondsInDay = secondsInHour * 24;
    const secondsInMonth = secondsInDay * 30; // 30-day month

    let remaining = durationSeconds;

    const months = Math.floor(remaining / secondsInMonth);
    remaining = remaining % secondsInMonth;

    const days = Math.floor(remaining / secondsInDay);
    remaining = remaining % secondsInDay;

    const hours = Math.floor(remaining / secondsInHour);
    remaining = remaining % secondsInHour;

    const minutes = Math.floor(remaining / secondsInMinute);

    const parts: string[] = [];
    if (months > 0) parts.push(`${months}mo`);
    if (days > 0) parts.push(`${days}d`);
    if (hours > 0) parts.push(`${hours}h`);
    if (minutes > 0 || parts.length === 0) parts.push(`${minutes}m`);

    return parts.join(' ');
};

export const customDateColumnType: GridColTypeDef = {
    extendType: 'string',
    filterOperators: customDateFilterOperators
};

const containsOperator = getGridStringOperators().filter((operator) => operator.value === 'contains');

const PreCogLinkRenderer = (params: GridCellParams) => {
    const { preCogId, id } = params.row;

    if (preCogId !== id) {
        return (
            <a
                href={`https://precog.prod-sc.adt.ats.amazon.dev/event/${preCogId}`}
                target='_blank'
                rel='noopener noreferrer'
            >
                {params.value}
            </a>
        );
    }

    // IDs are equal → no link
    return <span>{params.value}</span>;
};

const PreCogIdColumnHeaderRenderer = (params: GridColumnHeaderParams) => {
    return (
        <div style={{ display: 'flex', alignItems: 'center', gap: 4 }}>
            <OverlayTrigger
                trigger='click'
                rootClose
                placement='right'
                delay={{ show: 250, hide: 400 }}
                overlay={
                    <Popover id='precog-tip' style={{ padding: 5 }}>
                        <Popover.Content>
                            Redirects to PreCog.{' '}
                            <a
                                href={SITE_SHUTDOWN_PRECOG_TICKET_CREATION_LINK}
                                target='_blank'
                                rel='noopener noreferrer'
                            >
                                Request access in PreCog if needed
                            </a>
                            .
                        </Popover.Content>
                    </Popover>
                }
            >
                <AiOutlineInfoCircle
                    style={{ cursor: 'pointer' }}
                    size={FILTER_TIP_ICON_SIZE}
                    className='mb-sm-1 mr-sm-1'
                />
            </OverlayTrigger>
            <span className='MuiDataGrid-columnHeaderTitle'>{params.colDef.headerName}</span>
        </div>
    );
};

// prop for data grid columns
export const siteShutdownColumns: GridColDef[] = [
    {
        field: 'preCogId',
        headerName: 'PreCog ID',
        flex: 1,
        filterOperators: containsOperator, // Restrict operator
        filterable: false,
        sortable: false,
        renderCell: PreCogLinkRenderer,
        renderHeader: PreCogIdColumnHeaderRenderer
    },
    { field: 'siteCountry', headerName: 'Country', flex: 0.8, filterOperators: containsOperator },
    { field: 'siteState', headerName: 'State', flex: 0.6, filterOperators: containsOperator },
    { field: 'siteCity', headerName: 'City', flex: 0.8, filterOperators: containsOperator },
    {
        field: 'siteName',
        headerName: 'Sites',
        flex: 0.8,
        filterOperators: containsOperator,
        renderCell: (params) => (
            <a
                href={SITEID_FACILITY_MANAGER_LINK + `${params.row.siteId}`}
                target='_blank'
                rel='noopener noreferrer'
            >
                {params.value}
            </a>
        )
    },
    {
        field: 'facilityType',
        headerName: 'Facility Type',
        flex: 0.8,
        filterOperators: containsOperator,
        renderCell: (params) => <span>{params.value ?? 'N/A'}</span>
    },
    {
        field: 'businessLine',
        headerName: 'Business Line',
        flex: 0.8,
        filterOperators: containsOperator,
        renderCell: (params) => <span>{params.value ?? 'N/A'}</span>
    },
    {
        field: 'status',
        headerName: 'Status',
        flex: 0.8,
        filterOperators: containsOperator,
        filterable: false,
        renderCell: (params) => (
            <div
                style={{
                    backgroundColor: getStatusColor(params.value as ShutdownStatus),
                    borderRadius: '16px',
                    padding: '4px 12px',
                    color: '#ffffff',
                    fontWeight: 'bold',
                    fontSize: '12px',
                    whiteSpace: 'nowrap',
                    overflow: 'hidden',
                    textOverflow: 'ellipsis'
                }}
            >
                {params.value}
            </div>
        )
    },
    {
        field: 'startTime',
        headerName: 'Start',
        type: 'customDate',
        flex: 1.1,
        valueFormatter: (params) => (params.value ? formatToLocalTime(DateTime.fromISO(params.value)) : '')
    },
    {
        field: 'endTime',
        headerName: 'End',
        type: 'customDate',
        flex: 1.1,
        valueFormatter: (params) => (params.value ? formatToLocalTime(DateTime.fromISO(params.value)) : '')
    },
    {
        field: 'reason',
        headerName: 'Reasons',
        flex: 1,
        filterOperators: containsOperator
    },
    {
        field: 'siteType',
        headerName: 'Shutdown Areas',
        flex: 0.9,
        filterOperators: containsOperator
    },
    {
        field: 'duration',
        headerName: 'Duration',
        flex: 1,
        filterOperators: containsOperator,
        filterable: false,
        valueFormatter: (params) => durationToString(params.value)
    },
    {
        field: 'lastUpdateTime',
        headerName: 'Last Update Time',
        type: 'customDate', // Uses custom date operators
        flex: 1.1,
        valueFormatter: (params) => (params.value ? formatToLocalTime(DateTime.fromMillis(params.value)) : '')
    }
];

export type SortModel = {
    field: string;
    sort: GridSortDirection;
};

export const quickFiltersSortOrder: Record<ShutdownStatus, SortModel> = {
    Scheduled: { field: 'startTime', sort: 'desc' },
    Upcoming: { field: 'startTime', sort: 'desc' },
    Ongoing: { field: 'duration', sort: 'asc' },
    Reopened: { field: 'endTime', sort: 'desc' },
    Completed: { field: 'endTime', sort: 'desc' },
    Canceled: { field: 'endTime', sort: 'desc' }
};

export const buttonBaseProps: ButtonProps = {
    color: 'primary',
    size: 'small',
    startIcon: <ExportIcon />
};
